import { useEffect, useRef } from 'react';
import anime from 'animejs/lib/anime.es.js';

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const useScrollReveal = (options = {}) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion()) {
      el.style.opacity = '1';
      el.style.transform = 'none';
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          anime({
            targets: el,
            translateY: [40, 0],
            opacity: [0, 1],
            duration: options.duration || 900,
            delay: options.delay || 0,
            easing: options.easing || 'easeOutExpo',
          });
          observer.unobserve(el);
        }
      },
      { threshold: options.threshold || 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return ref;
};

export const useStaggerReveal = (selector, options = {}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (prefersReducedMotion()) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          anime({
            targets: container.querySelectorAll(selector),
            translateY: [30, 0],
            opacity: [0, 1],
            duration: options.duration || 800,
            delay: anime.stagger(options.stagger || 80, { start: options.start || 0 }),
            easing: 'easeOutExpo',
          });
          observer.unobserve(container);
        }
      },
      { threshold: 0.05 }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return containerRef;
};
